/** UXP-00016 — Mobile Companion framework (mirrors pkg/mobilecompanion) */

export const UXP_MC_ID = 'UXP-00016';

export interface DashboardKPI {
  label: string;
  value: string;
  change: string;
  positive: boolean;
}

export interface BriefingRow {
  type: string;
  title: string;
  status: string;
  duration: string;
}

export interface NotificationRow {
  category: string;
  title: string;
  priority: string;
  time: string;
  read: boolean;
}

export interface ApprovalRow {
  type: string;
  title: string;
  amount: string;
  priority: string;
  waiting: string;
}

export interface CompanionSessionRow {
  capability: string;
  question: string;
  response: string;
}

export interface VoiceCommandRow {
  feature: string;
  command: string;
  status: string;
}

export interface CrisisEventRow {
  type: string;
  title: string;
  severity: string;
  status: string;
}

export interface OfflineItemRow {
  feature: string;
  title: string;
  size: string;
  updated: string;
}

export const DASHBOARD_METRICS = [
  'Business Health', 'Revenue', 'Profit', 'Advertising', 'Inventory', 'Orders',
  'Cash Position', 'Executive Score', 'Pending Approvals', 'Critical Alerts',
];

export const BRIEFING_TYPES = [
  'Morning Brief', 'Afternoon Brief', 'Evening Brief', 'Voice Brief', 'Offline Brief', 'Travel Brief',
];

export const NOTIFICATION_CATEGORIES = [
  'Critical Alerts', 'Revenue Alerts', 'Inventory Alerts', 'Advertising Alerts', 'Finance Alerts',
  'Security Alerts', 'Compliance Alerts', 'AI Recommendations', 'Grouped Notifications',
];

export const APPROVAL_TYPES = [
  'Campaign Approval', 'Publishing Approval', 'Budget Approval', 'Purchase Approval',
  'Workflow Approval', 'Emergency Approval', 'Role-based Approval',
];

export const COMPANION_CAPABILITIES = [
  'Natural Conversations', 'Voice Chat', 'Business Questions', 'Forecast Requests',
  'Report Requests', 'Executive Guidance',
];

export const VOICE_FEATURES = [
  'Speech Recognition', 'Voice Commands', 'Voice Search', 'Voice Reports',
  'Voice Approvals', 'Speech Synthesis',
];

export const CRISIS_TYPES = [
  'Revenue Drops', 'Inventory Risk', 'Advertising Failures', 'Marketplace Issues',
  'Supplier Delays', 'Security Events', 'Emergency Response',
];

export const OFFLINE_FEATURES = [
  'Offline Dashboard', 'Cached Reports', 'Approval Queue', 'Knowledge Access', 'Conversation History',
];

export const SECURITY_FEATURES = [
  'Biometric Authentication', 'Device Trust', 'Remote Logout', 'Encrypted Storage', 'Session Protection',
];

export const MOBILE_ANALYTICS = [
  'Usage', 'Approval Speed', 'Decision Speed', 'Notification Effectiveness', 'AI Adoption',
];

export const COLLABORATION_FEATURES = [
  'Chat', 'Mentions', 'Voice Notes', 'Comments', 'Assignments', 'Approvals',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Voice Navigation', 'Screen Reader Support', 'Large Fonts',
  'High Contrast', 'Reduced Motion', 'One-Hand Operation',
];

export const ARCHITECTURE_STAGES = [
  'Executive Mobile Dashboard', 'AI Briefings', 'Notification Center', 'Executive Approvals',
  'AI Companion', 'Voice Experience', 'Crisis Center', 'Offline Experience',
];

export const LIVE_ROUTES: Record<string, string> = {
  executiveHome: '/platform/executive-home',
  command: '/command',
  copilot: '/copilot',
  dailyOps: '/executive/daily-ops',
};

export const DEMO_KPIS: DashboardKPI[] = [
  { label: 'Business Health', value: '88/100', change: '+4', positive: true },
  { label: 'Revenue', value: '₹48.2L', change: '+12%', positive: true },
  { label: 'Profit', value: '₹14.6L', change: '+8%', positive: true },
  { label: 'Advertising', value: '4.2x ROAS', change: '+0.3x', positive: true },
  { label: 'Inventory', value: '42 days', change: 'optimal', positive: true },
  { label: 'Orders', value: '842 today', change: '+18%', positive: true },
  { label: 'Cash Position', value: '₹12.8L', change: '+5%', positive: true },
  { label: 'Executive Score', value: '91/100', change: '+3', positive: true },
  { label: 'Pending Approvals', value: '5', change: '2 urgent', positive: false },
  { label: 'Critical Alerts', value: '2', change: '1 new', positive: false },
];

export const DEMO_BRIEFINGS: BriefingRow[] = [
  { type: 'Morning Brief', title: 'Morning Brief — Aug 1', status: 'ready', duration: '3 min' },
  { type: 'Voice Brief', title: 'Voice Brief — Commute', status: 'ready', duration: '2 min' },
  { type: 'Travel Brief', title: 'Travel Brief — Mumbai trip', status: 'scheduled', duration: '4 min' },
  { type: 'Offline Brief', title: 'Offline Brief — Cached', status: 'available', duration: '3 min' },
];

export const DEMO_NOTIFICATIONS: NotificationRow[] = [
  { category: 'Critical Alerts', title: 'Hub Gen 3 stockout risk in 8 days', priority: 'critical', time: '12m ago', read: false },
  { category: 'Revenue Alerts', title: 'Revenue +12% vs yesterday — Prime Day prep on track', priority: 'info', time: '1h ago', read: true },
  { category: 'Inventory Alerts', title: '412 aging units — storage fee spike in 14 days', priority: 'high', time: '2h ago', read: false },
  { category: 'AI Recommendations', title: 'Approve +15% ad budget on 5.1x ROAS campaigns', priority: 'medium', time: '3h ago', read: true },
  { category: 'Finance Alerts', title: 'Settlement variance SET-2026-0714 — ₹60K gap', priority: 'high', time: '4h ago', read: false },
];

export const DEMO_APPROVALS: ApprovalRow[] = [
  { type: 'Budget Approval', title: 'Prime Day ad budget increase (+15%)', amount: '₹2.4L', priority: 'high', waiting: '2h' },
  { type: 'Purchase Approval', title: 'PO-2026-0841 Hub Gen 3 replenishment', amount: '₹8.2L', priority: 'high', waiting: '4h' },
  { type: 'Publishing Approval', title: 'Publish 12 optimized listings', amount: '—', priority: 'medium', waiting: '6h' },
  { type: 'Campaign Approval', title: 'Launch win-back campaign — 56 customers', amount: '₹42K', priority: 'medium', waiting: '1d' },
];

export const DEMO_COMPANION: CompanionSessionRow[] = [
  { capability: 'Business Questions', question: 'Why did profit drop 2% last week?', response: 'Storage fees +₹42K · Returns +18% · Partially offset by revenue +11%' },
  { capability: 'Forecast Requests', question: 'Revenue forecast for Prime Day?', response: 'Predicted ₹82L (+68% vs baseline) · 87% confidence · inventory cover sufficient' },
  { capability: 'Executive Guidance', question: 'Should I approve the PO today?', response: 'Recommend 5-day delay — cash flow constraint in 21 days if approved today' },
];

export const DEMO_VOICE: VoiceCommandRow[] = [
  { feature: 'Voice Commands', command: 'Show pending approvals', status: 'ready' },
  { feature: 'Voice Approvals', command: 'Approve Prime Day ad budget', status: 'requires_confirm' },
  { feature: 'Voice Reports', command: 'Read morning brief', status: 'ready' },
  { feature: 'Voice Search', command: 'Find settlement reconciliation SOP', status: 'ready' },
];

export const DEMO_CRISIS: CrisisEventRow[] = [
  { type: 'Inventory Risk', title: 'Hub Gen 3 stockout in 8 days', severity: 'critical', status: 'active' },
  { type: 'Revenue Drops', title: 'Earbuds Pro category -8% WoW', severity: 'medium', status: 'monitoring' },
  { type: 'Supplier Delays', title: 'Supplier ABC — 5-day shipment delay', severity: 'high', status: 'mitigating' },
];

export const DEMO_OFFLINE: OfflineItemRow[] = [
  { feature: 'Offline Dashboard', title: 'Executive Dashboard Snapshot', size: '2.4 MB', updated: '15m ago' },
  { feature: 'Approval Queue', title: 'Approval Queue (5 pending)', size: '128 KB', updated: '15m ago' },
  { feature: 'Cached Reports', title: 'Weekly Revenue Report', size: '840 KB', updated: '2h ago' },
  { feature: 'Knowledge Access', title: 'Prime Day Launch Playbook', size: '1.2 MB', updated: '1d ago' },
];

export function buildMobileCompanionDashboard() {
  return {
    dashboard_metrics: DASHBOARD_METRICS.length,
    briefing_types: BRIEFING_TYPES.length,
    notification_types: NOTIFICATION_CATEGORIES.length,
    approval_types: APPROVAL_TYPES.length,
    voice_features: VOICE_FEATURES.length,
  };
}
